import { CardBody, Card, CardHeader, ChipProps, Chip, User, Button, Alert } from "@heroui/react";
import { Icon } from "@iconify/react";
import { TRequest } from "@shared/entities";
import { DocumentTypeSchema, TDocumentType } from "@shared/entities/document";
import { bituahProviderOptionsLabels } from "@shared/entities/utils";
import { createContext, ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "src/store/store";
import { FileUtils } from "src/utils/FileUtils";

const RequestDetailsContext = createContext<TRequest | null>(null);

export const useRequestDetails = () => useContext(RequestDetailsContext);

export function RequestDetails({
	request,
	children,
}: {
	request: TRequest | null;
	children: ReactNode;
}) {
	return (
		<RequestDetailsContext.Provider value={request}>{children}</RequestDetailsContext.Provider>
	);
}

RequestDetails.Card = function RequestDetailsCard({ children }: { children: ReactNode }) {
	return <Card className="overflow-visible text-start">{children}</Card>;
};

function getStatusColor(status: TRequest["status"]): ChipProps["color"] {
	switch (status) {
		case "completed":
			return "success";
		case "draft":
			return "default";
		case "pending":
			return "secondary";
		case "change_required":
			return "warning";
		case "pending_doctor":
			return "secondary";
		case "approved_by_doctor":
			return "primary";
		case "rejected_by_doctor":
		case "rejected":
			return "danger";
		case "pending_external_documents":
			return "warning";
		default:
			return "default";
	}
}
RequestDetails.CardHeader = function RequestDetailsCardHeader() {
	const request = useRequestDetails();

	const { t } = useTranslation(["requestDetailsPage", "common"]);

	if (!request) return null;

	return (
		<CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
			<div>
				<h1 className="text-xl font-semibold">
					{t("requestDetailsPage:title")}:{" "}
					{t(`requestDetailsPage:requestType.${request.requestType}`)}
				</h1>
				<p className="text-small text-default-500">
					{t("common:requestId")} : {request.id}
				</p>
			</div>
			<div className="flex gap-2 w-full sm:w-auto">
				<div className="flex-grow">
					<p className="text-small text-default-500">{t("common:fields.creationDate")}</p>
					<p className="font-medium">
						{new Date(request.createdAt).toLocaleDateString("he-IL")}
					</p>
				</div>
				<Chip color={getStatusColor(request.status)} variant="flat" className="capitalize">
					{t(`common:requestStatus.${request.status}`)}
				</Chip>
			</div>
		</CardHeader>
	);
};

RequestDetails.CardBody = function RequestDetailsCardHeader({ children }: { children: ReactNode }) {
	const request = useRequestDetails();

	if (!request) return null;

	return <CardBody className="gap-6 text-start">{children}</CardBody>;
};

RequestDetails.ClientDetails = function ClientDetails() {
	const request = useRequestDetails();

	// const { t } = useTranslation(["requestDetailsPage", "common"]);
	const { t } = useTranslation(["requestDetailsPage", "common"]);

	if (!request) return null;
	const { city, street, apartmentNumber, houseNumber } = request.clientDetails;

	const address = [city, [street, houseNumber].filter(Boolean).join(" "), apartmentNumber && `דירה ${apartmentNumber}`]
		.filter(Boolean)
		.join(", ");
	return (
		<div className="space-y-4 text-start">
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
				<div className="flex items-center  gap-2">
					<Icon icon="lucide:calendar" className="text-default-500" />
					<div>
						<p className="text-small text-default-500">תאריך לידה</p>
						<p className="font-medium">
							{!!request.clientDetails.dob &&
								new Date(request.clientDetails.dob).toLocaleDateString("he-IL")}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Icon icon="lucide:users" className="text-default-500" />
					<div className="flex-grow">
						<p className="text-small text-default-500">{t("common:fields.idNumber")}</p>
						<p className="font-medium">{request?.clientDetails?.IDNumber}</p>
					</div>
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div>
					<p className="text-small text-default-500 mb-2">
						{t("common:fields.firstName")} {t("common:fields.lastName")}
					</p>
					<User
						name={`${request?.clientDetails?.firstName} ${request?.clientDetails?.lastName}`}
					/>
				</div>
				<div>
					<p className="text-small text-default-500 mb-2">{t("common:fields.sex")}</p>
					<div className="flex items-center gap-2">
						<Icon icon={"lucide:user"} />
						<span className="capitalize">{t(`common:sex.${request.clientDetails.sex}`)}</span>
					</div>
				</div>
				<div>
					<p className="text-small text-default-500 mb-2">{t("common:phone")}</p>
					<div className="flex items-center gap-2">
						<Icon icon={"lucide:phone"} />
						<span className="capitalize">{request.clientDetails.phone}</span>
					</div>
				</div>
				<div>
					<p className="text-small text-default-500 mb-2">{t("common:email")}</p>
					<div className="flex items-center gap-2">
						<Icon icon={"lucide:mail"} />
						<span className="">{request.clientDetails.email}</span>
					</div>
				</div>
				<div>
					<p className="text-small text-default-500 mb-2">
						{t("common:fields.healthInsurance")}
					</p>
					<div className="flex items-center gap-2">
						<Icon icon={"lucide:heart"} />
						<span className="capitalize">
							{request.clientDetails?.healthInsurance
								? bituahProviderOptionsLabels[
										request.clientDetails
											.healthInsurance as keyof typeof bituahProviderOptionsLabels
								  ]
								: "לא צוין"}
						</span>
					</div>
				</div>
			</div>

			<Card className="bg-content3 text-start">
				<CardBody className="text-start">
					<p className="text-small text-default-500 mb-2">{t("common:fields.address")}</p>
					<div className="space-y-1">{address}</div>
				</CardBody>
			</Card>
		</div>
	);
};

function getFileName(type: TDocumentType, t: any) {
	switch (type) {
		case DocumentTypeSchema.Enum.MEDICAL_SUMMARY:
			return t("common:documents.types.medicalSummary");
		case DocumentTypeSchema.Enum.MEDICATION_REPORT:
			return t("common:documents.types.medicationReport");
		case DocumentTypeSchema.Enum.DOCTOR_RECOMMENDATION:
			return t("common:documents.types.doctorRecommendation");
		case DocumentTypeSchema.Enum.CONSUMPTION_TABLE:
			return t("common:documents.types.consumptionTable");
		case DocumentTypeSchema.Enum.ID:
			return t("common:documents.types.id");
		case DocumentTypeSchema.Enum.CANNABIS_LICENSE:
			return t("common:documents.types.cannabisLicense");
		case DocumentTypeSchema.Enum.PRESCRIPTIONS:
			return t("common:documents.types.prescriptions");
		case DocumentTypeSchema.Enum.CUSTOM1:
			return t("common:documents.types.custom1");
		case DocumentTypeSchema.Enum.CUSTOM2:
			return t("common:documents.types.custom2");
		case DocumentTypeSchema.Enum.CUSTOM3:
			return t("common:documents.types.custom3");
		default:
			return "מסמך";
	}
}

const getFileIcon = (extension: string) => {
	switch (extension) {
		case "pdf":
			return "lucide:file-text";
		case "doc":
		case "docx":
			return "lucide:file-type";
		default:
			return "lucide:file";
	}
};

RequestDetails.ClientDocuments = function ClientDocuments() {
	const request = useRequestDetails();
	const { t } = useTranslation(["requestDetailsPage", "common"]);

	if (!request) return null;

	const {
		DOCTOR_RECOMMENDATION,
		ID,
		MEDICAL_SUMMARY,
		CUSTOM1,
		CUSTOM2,
		CUSTOM3,
		CANNABIS_LICENSE,
		MEDICATION_REPORT,
		CONSUMPTION_TABLE,
	} = request.documents ?? {};

	const docs = [DOCTOR_RECOMMENDATION, ID, MEDICAL_SUMMARY];

	if (CANNABIS_LICENSE) {
		docs.push(CANNABIS_LICENSE);
	}
	if (MEDICATION_REPORT) {
		docs.push(MEDICATION_REPORT);
	}
	if (CONSUMPTION_TABLE) {
		docs.push(CONSUMPTION_TABLE);
	}
	if (CUSTOM1) {
		docs.push(CUSTOM1);
	}
	if (CUSTOM2) {
		docs.push(CUSTOM2);
	}
	if (CUSTOM3) {
		docs.push(CUSTOM3);
	}

	return (
		<div>
			<h3 className="text-medium font-semibold mb-3">{t("common:documents.title")}</h3>
			<div className="space-y-2">
				{docs.map((doc: any) => (
					<div
						key={doc?.url || doc?.type}
						className="flex items-center justify-between p-3 rounded-lg bg-content3"
					>
						<div className="flex items-center gap-3">
							<Icon icon={getFileIcon(doc?.extension ?? "")} className="text-default-500" />
							<span>{getFileName(doc?.type, t)}</span>
						</div>
						<div className="flex gap-2">
							<Button
								size="sm"
								variant="flat"
								onPress={() => FileUtils.handlePreview({ url: doc.url })}
								startContent={<Icon icon="lucide:eye" />}
							>
								הצגה
							</Button>
							{doc?.pdfVersion && (
								<Button
									size="sm"
									variant="flat"
									color="secondary"
									startContent={<Icon icon="lucide:file-text" />}
									onPress={() => {
										FileUtils.handleDownload({
											url: doc?.pdfVersion?.url,
											name: doc?.pdfVersion?.name,
										});
									}}
								>
									הורד PDF
								</Button>
							)}
							<Button
								size="sm"
								variant="flat"
								startContent={<Icon icon="lucide:download" />}
								onPress={() => {
									if (!doc) return;
									FileUtils.handleDownload({
										url: doc.url,
										name: doc.name,
									});
								}}
							>
								הורדה
							</Button>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

RequestDetails.ClientDocumentsForClient = function ClientDocumentsForClient() {
	const request = useRequestDetails();
	const { t } = useTranslation(["requestDetailsPage", "common"]);

	if (!request || !request.documentsForClient) return null;

	const { CANNABIS_LICENSE, PRESCRIPTIONS, DOCTOR_RECOMMENDATION } =
		request.documentsForClient;

	const docs = [];
	if (CANNABIS_LICENSE) {
		docs.push(CANNABIS_LICENSE);
	}
	if (PRESCRIPTIONS) {
		docs.push(PRESCRIPTIONS);
	}
	if (DOCTOR_RECOMMENDATION) {
		docs.push(DOCTOR_RECOMMENDATION);
	}

	if (docs.length === 0) return null;

	return (
		<div>
			<h3 className="text-medium font-semibold mb-3">מסמכים ללקוח</h3>
			<div className="space-y-2">
				{docs.map((doc: any) => (
					<div
						key={doc?.url || doc?.type}
						className="flex items-center justify-between p-3 rounded-lg bg-success-50 border border-success-200"
					>
						<div className="flex items-center gap-3">
							<Icon icon={getFileIcon(doc?.extension ?? "")} className="text-success" />
							<span className="font-medium">{getFileName(doc?.type, t)}</span>
						</div>
						<div className="flex gap-2">
							<Button
								size="sm"
								variant="flat"
								color="success"
								onPress={() => FileUtils.handlePreview({ url: doc.url })}
								startContent={<Icon icon="lucide:eye" />}
							>
								הצגה
							</Button>
							{doc?.pdfVersion && (
								<Button
									size="sm"
									variant="flat"
									color="secondary"
									startContent={<Icon icon="lucide:file-text" />}
									onPress={() => {
										FileUtils.handleDownload({
											url: doc?.pdfVersion?.url,
											name: doc?.pdfVersion?.name,
										});
									}}
								>
									הורד PDF
								</Button>
							)}
							<Button
								size="sm"
								variant="flat"
								color="success"
								startContent={<Icon icon="lucide:download" />}
								onPress={() => {
									if (!doc) return;
									FileUtils.handleDownload({
										url: doc.url,
										name: doc.name,
									});
								}}
							>
								הורדה
							</Button>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

RequestDetails.DoctorMessage = function DoctorMessage() {
	const request = useRequestDetails();

	const { t } = useTranslation(["requestDetailsPage", "common"]);

	if (!request) return null;

	return (
		<div>
			<div>{t("common:doctorMessage")}</div>
			<div className="">{request.rejectMessageByDoctor ?? "אין"}</div>
		</div>
	);
};

RequestDetails.ApproveMessageByDoctor = function ApproveMessageByDoctor() {
	const request = useRequestDetails();

	if (!request || !request.approveMessageByDoctor) return null;

	return (
		<div>
			<h3 className="text-medium font-semibold mb-3">הערת אישור מהרופא</h3>
			<div className="p-4 rounded-lg bg-success-50 border border-success-200">
				<p className="text-success-800">{request.approveMessageByDoctor}</p>
			</div>
		</div>
	);
};

RequestDetails.AdminMessage = function AdminMessage() {
	const request = useRequestDetails();
	const { user } = useStore();

	if (!request) return null;

	// Show adminMessageForClient for clients, adminMessage for doctors/admins
	const messageToShow = user?.admin || user?.issuingDoctor || user?.recommendingDoctor 
		? request.adminMessage 
		: request.adminMessageForClient;

	if (!messageToShow) return null;

	const isForClient = !user?.admin && !user?.issuingDoctor && !user?.recommendingDoctor;

	return (
		<div>
			<h3 className="text-medium font-semibold mb-3">
				{isForClient ? "הודעה מהמערכת" : "הודעה מהאדמין"}
			</h3>
			<div className={`p-4 rounded-lg border ${
				isForClient 
					? "bg-warning-50 border-warning-200" 
					: "bg-primary-50 border-primary-200"
			}`}>
				<p className={isForClient ? "text-warning-800" : "text-primary-800"}>
					{messageToShow}
				</p>
			</div>
		</div>
	);
};

RequestDetails.DoctorDocuments = function DoctorDocuments() {
	const request = useRequestDetails();
	const { t } = useTranslation(["requestDetailsPage", "common"]);

	if (!request) return null;

	const getFileIcon = (type: string) => {
		switch (type) {
			case "pdf":
				return "lucide:file-text";
			case "doc":
			case "docx":
				return "lucide:file-type";
			default:
				return "lucide:file";
		}
	};

	// const docs = [DOCTOR_RECOMMENDATION, ID, MEDICAL_SUMMARY, CUSTOM, transportersIds];
	const docs = request.doctorDocuments ?? [];
	// if(documents.ne)

	return (
		<div>
			<h3 className="text-medium font-semibold mb-3">{t("common:documents.doctorDocuments")}</h3>
			<div className="space-y-2">
				{docs.map((doc: any) => (
					<div
						key={doc?.url || doc?.type}
						className="flex items-center justify-between p-3 rounded-lg bg-content3"
					>
						<div className="flex items-center gap-3">
							<Icon icon={getFileIcon(doc?.type ?? "")} className="text-default-500" />
							<span>{doc?.label || doc?.name || getFileName(doc?.type, t)}</span>
						</div>
						<div className="flex gap-2">
							<Button
								size="sm"
								variant="flat"
								onPress={() => FileUtils.handlePreview({ url: doc.url })}
								startContent={<Icon icon="lucide:eye" />}
							>
								הצגה
							</Button>
							{doc?.pdfVersion && (
								<Button
									size="sm"
									variant="flat"
									color="secondary"
									startContent={<Icon icon="lucide:file-text" />}
									onPress={() => {
										FileUtils.handleDownload({
											url: doc?.pdfVersion?.url,
											name: doc?.pdfVersion.name,
										});
									}}
								>
									הורד PDF
								</Button>
							)}
							<Button
								size="sm"
								variant="flat"
								startContent={<Icon icon="lucide:download" />}
								onPress={() => {
									if (!doc) return;
									FileUtils.handleDownload({
										url: doc.url,
										name: doc.name,
									});
								}}
							>
								הורדה
							</Button>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

RequestDetails.DosageCard = function DosageCard() {
	const request = useRequestDetails();
	const { t } = useTranslation(["requestDetailsPage", "common"]);

	if (!request) return null;

	const dosageTypeLabels: Record<string, string> = {
		flowering: "תפרחת קנאביס",
		oil: "שמן קנאביס",
	};

	const { dosages = [], existsDosage = [] } = request;
	return (
		<div className="space-y-8">
			{!!existsDosage && existsDosage.length > 0 && (
				<div className="space-y-4">
					{/* Existing Dosage Header */}
					<div className="space-y-2 pb-3 border-b-2 border-default-300">
						<div className="flex items-center gap-3">
							<div className="flex items-center justify-center w-10 h-10 rounded-full bg-default-100">
								<Icon icon="lucide:check-circle-2" className="text-default-600 h-6 w-6" />
							</div>
							<h3 className="text-lg font-bold text-default-800">
								{t("common:dosageInfo.existingDosage")}
							</h3>
						</div>
						<p className="text-sm text-default-600 pr-12 leading-relaxed">
							כאן מוצג המינון הנוכחי של הלקוח שאושר ברישיון הקנאביס הרפואי הקיים שלו. זה כולל את הכמויות והריכוזים שאושרו ללקוח בעבר ומשמש כבסיס לבקשה החדשה.
						</p>
					</div>
					
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						{existsDosage.map((dosage, index) => (
							<Card 
								key={index} 
								className="bg-gradient-to-br from-default-50 to-default-100 border-l-4 border-l-default-400 shadow-sm"
							>
								<CardBody className="p-4">
									<div className="flex flex-col gap-3">
										<div className="flex items-center justify-between">
											<Chip
												color={dosage.type === "flowering" ? "success" : "primary"}
												variant="flat"
												size="sm"
												startContent={
													<Icon
														icon={
															dosage.type === "flowering"
																? "lucide:flower"
																: "lucide:droplet"
														}
														className="h-3.5 w-3.5"
													/>
												}
											>
												{dosageTypeLabels[dosage.type]}
											</Chip>
											<div className="text-right">
												<span className="text-2xl font-bold text-default-900 block">
													{dosage.amount}
												</span>
												<span className="text-xs text-default-600">
													{t("common:dosageInfo.grams")}
												</span>
											</div>
										</div>

										<div className="flex justify-between items-center pt-2 border-t border-default-200">
											<div className="flex items-center gap-1.5">
												<Icon icon="lucide:flask-conical" className="h-4 w-4 text-default-500" />
												<span className="text-sm text-default-600">
													{t("common:dosageInfo.concentration")}
												</span>
											</div>
											<span className="font-semibold text-default-800">{dosage.centralism}</span>
										</div>
									</div>
								</CardBody>
							</Card>
						))}
					</div>
				</div>
			)}

			{dosages.length > 0 && (
				<div className="space-y-4">
					{/* Requested Dosage Header */}
					<div className="space-y-2 pb-3 border-b-2 border-primary-300">
						<div className="flex items-center gap-3">
							<div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100">
								<Icon icon="lucide:arrow-right-circle" className="text-primary-600 h-6 w-6" />
							</div>
							<h3 className="text-lg font-bold text-primary-700">
								{t("common:dosageInfo.requestedDosage")}
							</h3>
						</div>
						<p className="text-sm text-primary-600 pr-12 leading-relaxed">
							כאן מוצג המינון המבוקש של הלקוח בבקשה הנוכחית. זה כולל את הכמויות והריכוזים החדשים שהלקוח מבקש לקבל. הבקשה תועבר לבדיקת הרופא המאשר לאישור או שינוי.
						</p>
					</div>
					
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						{dosages.map((dosage, index) => (
							<Card 
								key={index} 
								className="bg-gradient-to-br from-primary-50 to-primary-100 border-l-4 border-l-primary-500 shadow-md hover:shadow-lg transition-shadow"
							>
								<CardBody className="p-4">
									<div className="flex flex-col gap-3">
										<div className="flex items-center justify-between">
											<Chip
												color={dosage.type === "flowering" ? "success" : "primary"}
												variant="flat"
												size="sm"
												startContent={
													<Icon
														icon={
															dosage.type === "flowering"
																? "lucide:flower"
																: "lucide:droplet"
														}
														className="h-3.5 w-3.5"
													/>
												}
											>
												{dosageTypeLabels[dosage.type]}
											</Chip>
											<div className="text-right">
												<span className="text-2xl font-bold text-primary-700 block">
													{dosage.amount}
												</span>
												<span className="text-xs text-primary-600">
													{t("common:dosageInfo.grams")}
												</span>
											</div>
										</div>

										<div className="flex justify-between items-center pt-2 border-t border-primary-200">
											<div className="flex items-center gap-1.5">
												<Icon icon="lucide:flask-conical" className="h-4 w-4 text-primary-500" />
												<span className="text-sm text-primary-600">
													{t("common:dosageInfo.concentration")}
												</span>
											</div>
											<span className="font-semibold text-primary-700">{dosage.centralism}</span>
										</div>
									</div>
								</CardBody>
							</Card>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

RequestDetails.RecommendationOnlyAlert = function RecommendationOnlyAlert() {
	const request = useRequestDetails();

	if (!request || !request.isRecommendationOnly) return null;

	return (
		<Alert
			color="warning"
			variant="solid"
			startContent={<Icon icon="lucide:info" className="h-5 w-5" />}
			className="mb-4"
		>
			<div className="flex flex-col gap-1">
				<p className="font-semibold">בקשה להמלצה בלבד</p>
				<p className="text-sm">
					בקשה זו נועדה לקבלת המלצה רפואית בלבד ואינה כוללת אישור רישיון קנאביס רפואי
				</p>
			</div>
		</Alert>
	);
};
